import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { CSSProperties } from "react";

import { cn } from "../ui/cn";
import { tableFromClipboard } from "./paste-table";
import { cleanPastedHtml } from "./paste-html";
import { plainPasteText } from "./paste-text";

/**
 * A minimal rich-text surface: a contentEditable plus the few commands a
 * support reply actually needs.
 *
 * Built on `document.execCommand`, which is formally deprecated and has no
 * replacement. Every alternative is a large editor framework (TipTap, Lexical,
 * ProseMirror) that reimplements the same operations over a custom document
 * model. For bold/italic/underline and two list types that is a great deal of
 * dependency for very little, and execCommand is still implemented by every
 * current browser. If this ever needs tables, mentions or collaborative
 * editing, that is the point to adopt a real editor rather than extend this.
 *
 * The HTML produced here is agent-authored and goes straight into an email, so
 * it is sanitised server-side on write -- see the reply handler. Nothing here
 * should be treated as the security boundary.
 */

export interface RichTextHandle {
  /** Insert HTML at the caret, as a paste would. */
  insertHtml: (html: string) => void;
  /** Replace the `length` characters before the caret, then insert. */
  replaceBeforeCaret: (length: number, html: string) => void;
  focus: () => void;
  /**
   * Focus, and put the caret back where it last was in this editor.
   *
   * What `focus()` alone cannot do. Focusing a contentEditable that has lost
   * its selection collapses the caret to the very start of the element, so
   * anything inserted afterwards lands at the top of the message rather than
   * where the agent was typing.
   */
  restoreCaret: () => void;
  clear: () => void;
  /** Plain text before the caret in the current node, for `#` detection. */
  textBeforeCaret: () => string;
  /** Viewport rect of the caret, for anchoring a popover to it. */
  caretRect: () => DOMRect | null;
}

interface RichTextProps {
  /**
   * Content to start with, applied once on mount.
   *
   * This editor is uncontrolled -- the DOM is the source of truth while the
   * agent types -- so there is deliberately no prop that keeps pushing content
   * in. Anything doing that would fight the caret on every keystroke. Callers
   * that need to replace the content remount with a new `key`, which is what
   * makes mount the right and only moment to seed it.
   */
  initialHtml?: string;
  onInput: (html: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  /**
   * Files pasted into the surface -- a screenshot off the clipboard, most
   * often. Handed up rather than inlined here: the owner already has the
   * upload path that drag-and-drop uses, and an image belongs in it. Inlining
   * would mean a data: URL, which the server's sanitiser drops on sight.
   */
  onPasteFiles?: (files: File[]) => void;
  /**
   * Keep pasted HTML exactly as it came, instead of cleaning it.
   *
   * Off for replies, where a paste keeps its bold, lists and links but loses
   * the fonts, colours and layout that would make the reply look nothing like
   * the rest of the desk's mail -- see `paste-html.ts`. On for the signature
   * editor, where the appearance *is* the point: a signature copied out of
   * Gmail is a small HTML document, and cleaning it is exactly how a logo and
   * a layout get lost.
   *
   * Safe either way -- everything is sanitised server-side on save.
   */
  allowRichPaste?: boolean;
  /**
   * A formatted paste has just landed, with the surface's new HTML.
   *
   * The signature editor uses it to adopt the images that came with it: a
   * pasted signature's logo is an absolute URL on the sending client's server,
   * and nobody finds out it is unreachable until a customer opens the mail.
   * Fired after the insert, so what it receives is the merged content rather
   * than the fragment.
   */
  onRichPaste?: (html: string) => void;
  placeholder: string;
  /**
   * How tall the surface is, as Tailwind height classes.
   *
   * A prop rather than something `className` overrides, because `cn` is plain
   * clsx with no Tailwind-aware merging: passing a second `min-h-*` would emit
   * both and let the stylesheet's order decide, which is not a thing a caller
   * can reason about. One set of height classes in, one set out.
   *
   * A `max-h-*` belongs here too where the caller can be seeded with content
   * it did not size for. The surface already scrolls; without a ceiling a
   * long quoted reply simply grows, and takes the conversation and the send
   * button off the bottom of the screen with it.
   */
  surfaceHeight?: string;
  /**
   * An exact height in pixels, overriding the classes above.
   *
   * How a dragged composer keeps its height: a size somebody chose is a number,
   * and there is no Tailwind class for 237px. Callers that pass this pass
   * `surfaceHeight=""` with it -- a `min-h-*` left in place would win over a
   * shorter chosen height and the handle would appear to stop working part of
   * the way down.
   */
  surfaceStyle?: CSSProperties;
  className?: string;
}

export const RichText = forwardRef<RichTextHandle, RichTextProps>(
  function RichText(
    {
      initialHtml,
      onInput,
      onKeyDown,
      onPasteFiles,
      allowRichPaste,
      onRichPaste,
      placeholder,
      surfaceHeight = "min-h-20 sm:min-h-28",
      surfaceStyle,
      className,
    },
    ref,
  ) {
    const host = useRef<HTMLDivElement>(null);

    /**
     * Where the caret last was inside this editor.
     *
     * Every menu in the toolbar takes focus away: the emoji picker autofocuses
     * its search box, the link popover has fields of its own. Focus leaving a
     * contentEditable discards its selection, and re-focusing puts the caret at
     * the start of the element -- so an emoji chosen after writing three
     * paragraphs was inserted above all three. Remembering the range is the
     * only way to put it back.
     */
    const caret = useRef<Range | null>(null);

    useEffect(() => {
      if (!initialHtml || !host.current) return;
      host.current.innerHTML = initialHtml;
      // Tell the owner what it now holds, so a restored draft counts as content
      // without the agent having to type a character first.
      onInput(initialHtml);
      // Mount only, by design -- see initialHtml.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
     * Tracked on the document rather than with focus/blur handlers, because the
     * caret also moves by arrow key and by drag, neither of which is a focus
     * event. Ranges from outside this editor are ignored, so one editor never
     * captures another's caret -- or the emoji search box's.
     */
    useEffect(() => {
      const onSelectionChange = (): void => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        if (!host.current?.contains(range.commonAncestorContainer)) return;

        caret.current = range.cloneRange();
      };

      document.addEventListener("selectionchange", onSelectionChange);
      return () =>
        document.removeEventListener("selectionchange", onSelectionChange);
    }, []);

    /**
     * Put the caret back before writing at it.
     *
     * Falls back to the end of the content, not the start. Somebody who has
     * never clicked into the box and picks an emoji means "add one", and the
     * end is where adding happens; the start is never what was wanted.
     */
    const restoreCaret = (): void => {
      const node = host.current;
      if (!node) return;

      node.focus();

      const selection = window.getSelection();
      if (!selection) return;

      const saved = caret.current;
      // Re-checked against the DOM: a remount or a cleared editor can leave a
      // saved range pointing at nodes that are no longer in the document, and
      // restoring one of those throws.
      if (saved && node.contains(saved.commonAncestorContainer)) {
        selection.removeAllRanges();
        selection.addRange(saved);
        return;
      }

      const end = document.createRange();
      end.selectNodeContents(node);
      end.collapse(false);
      selection.removeAllRanges();
      selection.addRange(end);
    };

    /** Remember where the insert left the caret, so the next one follows it. */
    const rememberCaret = (): void => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      if (host.current?.contains(range.commonAncestorContainer)) {
        caret.current = range.cloneRange();
      }
    };

    useImperativeHandle(ref, () => ({
      focus: () => host.current?.focus(),

      restoreCaret,

      clear: () => {
        if (host.current) host.current.innerHTML = "";
        caret.current = null;
      },

      insertHtml: (html) => {
        restoreCaret();
        document.execCommand("insertHTML", false, html);
        rememberCaret();
        if (host.current) onInput(host.current.innerHTML);
      },

      replaceBeforeCaret: (length, html) => {
        restoreCaret();
        const selection = window.getSelection();

        /*
         * Extend the selection backwards over the trigger text before
         * inserting, so "#returns" disappears rather than being left in front
         * of the snippet it summoned.
         */
        if (selection && selection.rangeCount > 0) {
          for (let i = 0; i < length; i += 1) {
            selection.modify("extend", "backward", "character");
          }
        }

        /*
         * A mention is placed by hand. Chrome's insertHTML "cleans up" a span
         * that carries no style when it lands inside a list item, keeping the
         * text and dropping the element -- so "@Acme" on a checklist line
         * arrived as plain words and filed nowhere. Everything else (canned
         * responses, which are blocks) still goes through insertHTML, which is
         * what keeps it on the undo stack.
         */
        if (selection && selection.rangeCount > 0 && html.startsWith("<span data-")) {
          document.execCommand("delete");
          const range = selection.getRangeAt(0);
          const fragment = range.createContextualFragment(html);
          const last = fragment.lastChild;
          range.insertNode(fragment);
          if (last) {
            range.setStartAfter(last);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } else {
          document.execCommand("insertHTML", false, html);
        }
        rememberCaret();
        if (host.current) onInput(host.current.innerHTML);
      },

      textBeforeCaret: () => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return "";

        const range = selection.getRangeAt(0);
        const node = range.startContainer;
        if (node.nodeType !== Node.TEXT_NODE) return "";

        return (node.textContent ?? "").slice(0, range.startOffset);
      },

      caretRect: () => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;

        const range = selection.getRangeAt(0).cloneRange();
        range.collapse(true);

        const rect = range.getBoundingClientRect();
        // A collapsed range at the start of an empty line reports all zeros;
        // fall back to the editor's own box so the popover still lands sanely.
        if (rect.width === 0 && rect.height === 0 && rect.top === 0) {
          return host.current?.getBoundingClientRect() ?? null;
        }
        return rect;
      },
    }));

    /* Set while a drag that started inside this editor is under way. */
    const draggingOwn = useRef(false);

    /**
     * Insert pasted or dropped content at the caret, the way this editor
     * takes it. One function for both routes, so the two cannot drift apart
     * again. In order:
     *
     * - Where the owner asked for it, the HTML as it came (the signature).
     * - A table, rebuilt as a clean grid, because flattened it is a paragraph
     *   of numbers. See `paste-table.ts`.
     * - Otherwise the HTML cleaned to what the toolbar could have made: bold,
     *   italic, underline, lists, links and lines, without the source's
     *   fonts and colours. See `paste-html.ts`.
     * - And plain text when there is no HTML at all -- a plain-text source,
     *   or Cmd/Ctrl+Shift+V, which browsers deliver as a paste carrying
     *   text/plain alone. That is the agent asking for no formatting, and it
     *   is the way to get it.
     */
    const insertTransfer = (data: DataTransfer, target: HTMLDivElement): void => {
      const text = plainPasteText(data.getData("text/plain"));
      const sourceHtml = data.getData("text/html");
      const rich = allowRichPaste ? sourceHtml : "";

      const table = rich ? null : tableFromClipboard(sourceHtml, text);
      const cleaned =
        rich || table || !sourceHtml ? null : cleanPastedHtml(sourceHtml, text);

      if (rich) {
        document.execCommand("insertHTML", false, rich);
      } else if (table) {
        document.execCommand("insertHTML", false, table);
      } else if (cleaned) {
        document.execCommand("insertHTML", false, cleaned);
      } else {
        document.execCommand("insertText", false, text);
      }

      const merged = target.innerHTML;
      onInput(merged);
      if (rich) onRichPaste?.(merged);
    };

    return (
      <div
        ref={host}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        data-placeholder={placeholder}
        suppressContentEditableWarning
        onInput={(event) => onInput(event.currentTarget.innerHTML)}
        onKeyDown={(event) => {
          /*
           * Tab indents, Shift+Tab outdents -- inside a list that nests a
           * bullet under its parent, and in ordinary text it steps the block
           * across, which is what Gmail does and what people expect from a
           * message box.
           *
           * It costs Tab as a way out of the editor, so Escape then Tab is the
           * keyboard escape hatch (the owner's handler closes any open menu
           * first, and focus moves on from there). That trade is deliberate:
           * a text box people write structured replies in is one of the few
           * places where Tab-as-indent beats Tab-as-navigation.
           */
          if (event.key === "Tab" && !event.metaKey && !event.ctrlKey) {
            // The owner's inline menus claim Tab to accept a suggestion; let
            // that run first and only indent when nothing intercepted it.
            onKeyDown?.(event);
            if (event.defaultPrevented) return;

            event.preventDefault();
            document.execCommand(event.shiftKey ? "outdent" : "indent", false);
            rememberCaret();
            if (host.current) onInput(host.current.innerHTML);
            return;
          }

          onKeyDown?.(event);
        }}
        /*
         * Paste what was said, not how it looked. Pasting from Word or a web
         * page otherwise drags in fonts, colours and nested tables that make
         * the reply look nothing like the rest of the desk's mail -- and which
         * the server would strip unevenly anyway. It once pasted plain text
         * outright, which lost the bullets and bold the desk wanted kept; see
         * `insertTransfer` for what survives now.
         */
        onPaste={(event) => {
          /*
           * Files first: a screenshot on the clipboard has no useful text
           * form, and falling through would paste its filename or nothing at
           * all. Word and Outlook put an image alongside the copied text, so
           * this only claims the paste when there is no text to prefer.
           */
          const files = Array.from(event.clipboardData.files);
          const text = event.clipboardData.getData("text/plain");

          if (onPasteFiles && files.length > 0 && text === "") {
            event.preventDefault();
            onPasteFiles(files);
            return;
          }

          event.preventDefault();
          insertTransfer(event.clipboardData, event.currentTarget);
        }}
        /*
         * A drop is a paste by another route, and has to be treated as one.
         *
         * Left to the browser, text dragged in from Outlook, Word or a web page
         * landed as the HTML it came from -- the fonts, sizes and colours the
         * paste handler above exists to clean off, by the side door. So a drop
         * from outside is placed where it fell and then inserted exactly as a
         * paste would be.
         *
         * Two drops are left alone. Files go on up to `MessageEditor`, which
         * attaches them. And a drag that began in this editor is somebody
         * moving their own words about: the browser's move is the right thing
         * for that, and taking it over would turn every move into a copy.
         */
        onDragStart={() => {
          draggingOwn.current = true;
        }}
        onDragEnd={() => {
          draggingOwn.current = false;
        }}
        onDrop={(event) => {
          if (draggingOwn.current) return;
          if (event.dataTransfer.types.includes("Files")) return;
          const types = event.dataTransfer.types;
          if (!types.includes("text/plain") && !types.includes("text/html")) {
            return;
          }

          event.preventDefault();
          const target = event.currentTarget;
          target.focus();
          const at = rangeAtPoint(event.clientX, event.clientY);
          const selection = window.getSelection();
          if (at && target.contains(at.startContainer) && selection) {
            selection.removeAllRanges();
            selection.addRange(at);
          }
          insertTransfer(event.dataTransfer, target);
        }}
        style={surfaceStyle}
        className={cn(
          // `rich-text` restores list markers and paragraph spacing that
          // Tailwind's preflight removes -- see index.css.
          "rich-text",
          surfaceHeight,
          "w-full overflow-y-auto rounded-b-md px-3 py-2 text-sm outline-none",
          "bg-surface text-content",
          // The placeholder is CSS-only: a real one does not exist for
          // contentEditable, and rendering it as a child node would put it in
          // the value the moment somebody typed.
          "empty:before:pointer-events-none empty:before:text-faint empty:before:content-[attr(data-placeholder)]",
          className,
        )}
      />
    );
  },
);

/** The commands the toolbar exposes. */
export type Command =
  | "bold"
  | "italic"
  | "underline"
  | "insertUnorderedList"
  | "insertOrderedList"
  | "indent"
  | "outdent";

export function runCommand(command: Command): void {
  document.execCommand(command, false);
}

/**
 * True when the caret currently sits inside the given formatting.
 *
 * Indent and outdent are actions rather than states -- there is no "indented"
 * for the caret to be inside -- so they are always reported off, which is the
 * right answer for a toolbar button that toggles nothing.
 */
export function isCommandActive(command: Command): boolean {
  if (command === "indent" || command === "outdent") return false;

  try {
    return document.queryCommandState(command);
  } catch {
    // queryCommandState throws in some engines when nothing is focused.
    return false;
  }
}

/**
 * A collapsed range at a point on screen, for placing a drop.
 *
 * Chrome and Safari have only the older `caretRangeFromPoint`; Firefox has
 * only the standard `caretPositionFromPoint`, which newer Chrome also has.
 * Whichever exists, then -- and null where neither answers, when the drop
 * goes in at the caret instead, which is no worse than where it would have
 * landed.
 */
function rangeAtPoint(x: number, y: number): Range | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number,
    ) => { offsetNode: Node; offset: number } | null;
  };
  if (typeof doc.caretPositionFromPoint === "function") {
    const position = doc.caretPositionFromPoint(x, y);
    if (!position) return null;
    const range = document.createRange();
    range.setStart(position.offsetNode, position.offset);
    range.collapse(true);
    return range;
  }
  return document.caretRangeFromPoint?.(x, y) ?? null;
}
